import useInlineMap, { CoordinateTypes, LOCATION_FIELD_DEFAULT } from '../../hooks/use-inline-map'
import { defaultIcon } from '../../services/generic-search'
import { InlineMap } from '../aoraki/maps-react/src/components'
import { getLocationFieldtoolTipMessage } from './location-field-edit'
import LookupField from './lookup-field'
import { getBaseLayers } from '../../config/map'
import { FormField, FormFieldRow, Heading, ReadOnlyField } from '@msaf/core-react'
import { validateLatLng } from '@msaf/maps-common'

export type LocationFieldViewType = {
  labelId: string
  value: string
}
export interface LocationFieldViewProps {
  index?: number
  formFieldLabel?: string
  easting: LocationFieldViewType
  northing: LocationFieldViewType
  isLoading?: boolean
  isMapHidden?: boolean
  latitude: LocationFieldViewType
  longitude: LocationFieldViewType
  locationAccuracy?: string | number
  distanceBetweenProposedAndActualWell?: { value: number | undefined; canRender: boolean }
}

export default function LocationFieldView({
  formFieldLabel,
  easting,
  northing,
  latitude,
  longitude,
  isLoading,
  isMapHidden = false,
  locationAccuracy,
  distanceBetweenProposedAndActualWell,
}: LocationFieldViewProps) {
  const { startPosition } = useInlineMap({
    xCoordinate: easting.value ?? longitude.value,
    yCoordinate: northing.value ?? latitude.value,
    projectionType:
      !easting.value && !northing.value && validateLatLng(latitude.value, longitude.value)
        ? CoordinateTypes.LatLng
        : CoordinateTypes.NZTM,
  })

  return (
    <>
      {formFieldLabel && (
        <Heading isSkeleton={isLoading} level={4}>
          {formFieldLabel}
        </Heading>
      )}
      <FormFieldRow>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(longitude.labelId)}
          isSkeleton={isLoading}
          labelId={longitude.labelId}
          label={CoordinateTypes.Lng}
        >
          <ReadOnlyField isSkeleton={isLoading} labelledBy={longitude.labelId} value={`${longitude.value ?? ''}`} />
        </FormField>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(latitude.labelId)}
          isSkeleton={isLoading}
          labelId={latitude.labelId}
          label={CoordinateTypes.Lat}
        >
          <ReadOnlyField isSkeleton={isLoading} labelledBy={latitude.labelId} value={`${latitude.value ?? ''}`} />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(easting.labelId)}
          isSkeleton={isLoading}
          labelId={easting.labelId}
          label={CoordinateTypes.Easting}
        >
          <ReadOnlyField isSkeleton={isLoading} labelledBy={easting.labelId} value={`${easting.value ?? ''}`} />
        </FormField>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(northing.labelId)}
          isSkeleton={isLoading}
          labelId={northing.labelId}
          label={CoordinateTypes.Northing}
        >
          <ReadOnlyField isSkeleton={isLoading} labelledBy={northing.labelId} value={`${northing.value ?? ''}`} />
        </FormField>
      </FormFieldRow>
      {locationAccuracy && typeof locationAccuracy === 'string' && (
        <FormField isSkeleton={isLoading} labelId='locationQuality' label='Location quality'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='locationQuality' value={locationAccuracy} />
        </FormField>
      )}
      {distanceBetweenProposedAndActualWell?.canRender && (
        <FormField
          isSkeleton={isLoading}
          labelId='distanceBetweenProposedAndActualWell'
          label='Distance from proposed (m)'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='distanceBetweenProposedAndActualWell'
            value={distanceBetweenProposedAndActualWell?.value ?? '-'}
          />
        </FormField>
      )}
      {locationAccuracy && typeof locationAccuracy === 'number' && (
        <FormField isSkeleton={isLoading} label='Location quality' labelId='locationAccuracy'>
          <LookupField
            isSkeleton={isLoading}
            labelledBy='locationAccuracy'
            fieldId='locationAccuracy'
            value={`${locationAccuracy}`}
            setValue={() => {}}
            isDisabled
          />
        </FormField>
      )}
      {!isMapHidden && (
        <InlineMap
          isSkeleton={isLoading}
          position={startPosition}
          markerIcon={defaultIcon}
          getBaseLayers={getBaseLayers}
          zoom={startPosition ? LOCATION_FIELD_DEFAULT.LOCATION_SET : LOCATION_FIELD_DEFAULT.NO_LOCATION}
          isMarkerDraggable={false}
        />
      )}
    </>
  )
}
